"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Send,
  Mail,
  Phone,
  Building,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  ShieldCheck,
  Headphones,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchServices } from "@/store/slices/landingSlice";
import { post } from "@/services/ApiService";
import { CREATE_CONTACT_US } from "@/services/ApiRoutes";
import AlertDialog from "@/utils/AlertDialog";

const ContactFormSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";
  const dispatch = useAppDispatch();
  const { data: services, loading } = useAppSelector(
    (state) => state.landing.services,
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (submitError) setSubmitError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.message) {
      setSubmitError("Please provide your email address and message.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const fullName =
        `${formData.firstName} ${formData.lastName}`.trim() ||
        "Website Visitor";
      const response = await post(CREATE_CONTACT_US, {
        name: fullName,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        service: formData.service || "General Inquiry",
        message: formData.message.trim(),
      });

      if (
        response &&
        (response.success || response.data || response._id || response.message)
      ) {
        setSubmitSuccess(true);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          company: "",
          service: "",
          message: "",
        });

        AlertDialog(
          "Message Sent!",
          "Thank you for contacting Hubmicroo! Your inquiry has been received and our team will get back to you shortly.",
          "success",
          4000,
          false,
          false,
        );

        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        throw new Error(response?.message || "Failed to submit message");
      }
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to send message. Please try again or reach out via email.";
      setSubmitError(errorMsg);

      AlertDialog("Submission Error", errorMsg, "error", 4000, false, false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className={`py-16 sm:py-24 relative overflow-hidden transition-colors duration-700 ${
        isDark
          ? "bg-[#080D1A]"
          : "bg-gradient-to-b from-white via-purple-50/20 to-white"
      }`}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF]/40 to-transparent" />

      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-full filter blur-[120px] ${
            isDark ? "bg-[#8B31FF]/20" : "bg-[#8B31FF]/10"
          }`}
          animate={{
            scale: [1, 1.15, 1],
            x: [0, 40, 0],
            y: [0, 25, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full filter blur-[120px] ${
            isDark ? "bg-[#6D28D9]/20" : "bg-[#6D28D9]/8"
          }`}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, -40, 0],
            y: [0, -25, 0],
          }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Grid Pattern */}
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-700 ${
          isDark ? "opacity-[0.04]" : "opacity-[0.025]"
        }`}
      >
        <div
          className="w-full h-full"
          style={{
            backgroundImage: isDark
              ? `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`
              : `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left Side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#8B31FF]/30 bg-[#8B31FF]/10 backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-[#8B31FF] animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-wider text-[#8B31FF]">
                Get In Touch
              </span>
            </div>

            <h2
              className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Send Us a{" "}
              <span className="bg-gradient-to-r from-[#8B31FF] via-[#a855f7] to-[#8B31FF] bg-clip-text text-transparent">
                Message
              </span>
            </h2>

            <p
              className={`text-base sm:text-lg leading-relaxed ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Fill out the form and our team will get back to you within 24
              hours. We&apos;re excited to learn about your project and how we
              can help.
            </p>

            {/* Why Contact Us - Sleek cards */}
            <div className="space-y-3.5 pt-2">
              {[
                {
                  icon: Clock,
                  title: "Quick Response",
                  description:
                    "We typically respond within 2-4 hours during business hours.",
                },
                {
                  icon: Headphones,
                  title: "Expert Consultation",
                  description:
                    "Get personalized advice from our senior engineering team.",
                },
                {
                  icon: ShieldCheck,
                  title: "Free Quote & NDA",
                  description:
                    "Receive a detailed project estimate with complete confidentiality.",
                },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                    className={`flex items-center gap-4 p-3.5 sm:p-4 rounded-lg border transition-all duration-300 group ${
                      isDark
                        ? "bg-white/[0.03] border-white/8 hover:border-[#8B31FF]/40 hover:bg-white/[0.06]"
                        : "bg-white/80 border-gray-100 shadow-sm hover:border-[#8B31FF]/30 hover:shadow-md"
                    }`}
                  >
                    <div
                      className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                        isDark
                          ? "bg-[#8B31FF]/20 text-[#a855f7] group-hover:bg-[#8B31FF] group-hover:text-white"
                          : "bg-purple-100/70 text-[#8B31FF] group-hover:bg-[#8B31FF] group-hover:text-white"
                      }`}
                    >
                      <Icon className="w-5 h-5 transition-colors" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`text-sm sm:text-base font-bold truncate ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {item.title}
                      </p>
                      <p
                        className={`text-xs sm:text-sm ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 w-full"
          >
            <div
              className={`rounded-3xl p-6 sm:p-8 lg:p-10 border shadow-2xl backdrop-blur-xl transition-all duration-500 ${
                isDark
                  ? "bg-[#0E1526]/90 border-white/10 shadow-black/40"
                  : "bg-white/95 border-purple-100 shadow-[0_12px_40px_rgba(139,49,255,0.08)]"
              }`}
            >
              <div className="mb-6">
                <h3
                  className={`text-2xl sm:text-3xl font-bold tracking-tight ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Send Us a Message
                </h3>
                <p
                  className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  We typically reply within a few hours.
                </p>
              </div>

              {/* Success Notification */}
              {submitSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-6 p-4 rounded-lg flex items-center gap-3 border ${
                    isDark
                      ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                  }`}
                >
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  <p className="text-sm font-medium">
                    Thank you! Your message has been sent successfully. We will
                    reach out soon.
                  </p>
                </motion.div>
              )}

              {/* Error Notification */}
              {submitError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-6 p-4 rounded-lg flex items-center gap-3 border ${
                    isDark
                      ? "bg-rose-500/15 border-rose-500/30 text-rose-300"
                      : "bg-rose-50 border-rose-200 text-rose-800"
                  }`}
                >
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <p className="text-sm font-medium">{submitError}</p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* First & Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      disabled={submitting}
                      className={`w-full px-4 py-3 text-sm rounded-lg border outline-none transition-all ${
                        isDark
                          ? "bg-white/[0.04] border-white/10 text-white placeholder-gray-500 focus:border-[#8B31FF] focus:bg-white/[0.08]"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#8B31FF] focus:bg-white"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                      disabled={submitting}
                      className={`w-full px-4 py-3 text-sm rounded-lg border outline-none transition-all ${
                        isDark
                          ? "bg-white/[0.04] border-white/10 text-white placeholder-gray-500 focus:border-[#8B31FF] focus:bg-white/[0.08]"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#8B31FF] focus:bg-white"
                      }`}
                    />
                  </div>
                </div>

                {/* Email Address */}
                <div>
                  <label
                    className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    disabled={submitting}
                    className={`w-full px-4 py-3 text-sm rounded-lg border outline-none transition-all ${
                      isDark
                        ? "bg-white/[0.04] border-white/10 text-white placeholder-gray-500 focus:border-[#8B31FF] focus:bg-white/[0.08]"
                        : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#8B31FF] focus:bg-white"
                    }`}
                  />
                </div>

                {/* Phone & Company */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      disabled={submitting}
                      className={`w-full px-4 py-3 text-sm rounded-lg border outline-none transition-all ${
                        isDark
                          ? "bg-white/[0.04] border-white/10 text-white placeholder-gray-500 focus:border-[#8B31FF] focus:bg-white/[0.08]"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#8B31FF] focus:bg-white"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      disabled={submitting}
                      className={`w-full px-4 py-3 text-sm rounded-lg border outline-none transition-all ${
                        isDark
                          ? "bg-white/[0.04] border-white/10 text-white placeholder-gray-500 focus:border-[#8B31FF] focus:bg-white/[0.08]"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#8B31FF] focus:bg-white"
                      }`}
                    />
                  </div>
                </div>

                {/* Service Select */}
                <div>
                  <label
                    className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Select Service
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    disabled={submitting}
                    className={`w-full px-4 py-3 text-sm rounded-lg border outline-none transition-all cursor-pointer ${
                      isDark
                        ? "bg-[#0E1526] border-white/10 text-white focus:border-[#8B31FF]"
                        : "bg-gray-50 border-gray-200 text-gray-900 focus:border-[#8B31FF] focus:bg-white"
                    }`}
                  >
                    <option
                      value=""
                      className={
                        isDark
                          ? "bg-[#0E1526] text-white"
                          : "bg-white text-gray-900"
                      }
                    >
                      {loading ? "Loading services..." : "Select Service"}
                    </option>
                    {services.map((service, index) => (
                      <option
                        key={service._id || service.id || index}
                        value={service.title}
                        className={
                          isDark
                            ? "bg-[#0E1526] text-white"
                            : "bg-white text-gray-900"
                        }
                      >
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label
                    className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Message *
                  </label>
                  <textarea
                    rows={4}
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project..."
                    disabled={submitting}
                    className={`w-full px-4 py-3 text-sm rounded-lg border outline-none transition-all resize-none ${
                      isDark
                        ? "bg-white/[0.04] border-white/10 text-white placeholder-gray-500 focus:border-[#8B31FF] focus:bg-white/[0.08]"
                        : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#8B31FF] focus:bg-white"
                    }`}
                  />
                </div>

                {/* Submit Button */}
                <motion.button
                  whileHover={!submitting ? { scale: 1.01 } : {}}
                  whileTap={!submitting ? { scale: 0.99 } : {}}
                  type="submit"
                  disabled={submitting}
                  className="w-full cursor-pointer px-6 py-3.5 bg-gradient-to-r from-[#7C3AED] via-[#8B31FF] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#7C3AED] text-white rounded-lg font-bold text-base shadow-lg shadow-[#8B31FF]/25 hover:shadow-xl hover:shadow-[#8B31FF]/35 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Sending Message...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </motion.button>

                <p
                  className={`text-center text-xs transition-colors duration-500 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  We respect your privacy. Your information will never be
                  shared.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
